"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Group,
  Input,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { deleteOptionalAnalyticsData } from "@/lib/client/privacyControls";
import styles from "./styles.module.scss";

const FORM_NAME = "account-deletion-request";

type PrivacyActionsProps = {
  reloadPage?: () => void;
};

const PrivacyActions = ({ reloadPage = () => window.location.reload() }: PrivacyActionsProps) => {
  const [cookieStatus, setCookieStatus] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const reloadTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current);
    },
    []
  );

  const handleDeleteCookies = () => {
    deleteOptionalAnalyticsData();
    setCookieStatus("Optional analytics cookies and identifiers were deleted. Reloading…");
    if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(reloadPage, 600);
  };

  const handleDeletionRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormStatus("submitting");

    const form = event.currentTarget;
    const data = new FormData(form);
    data.set("submitted-at", new Date().toISOString());

    const body = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (typeof value === "string") body.append(key, value);
    }

    try {
      const response = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!response.ok) throw new Error("Deletion request failed");

      form.reset();
      setFormStatus("success");
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <Stack className={styles.privacyActions} gap="lg">
      <Box component="section" id="cookie-controls" className={styles.actionCard}>
        <Stack gap="sm">
          <Title order={2}>Quick cookie controls</Title>
          <Text>
            Delete KURB&apos;s optional analytics consent and the Google Analytics, Microsoft
            Clarity and KURB analytics identifiers accessible from this browser. This will not sign
            you out or remove saved KURB preferences.
          </Text>
          <Group>
            <Button type="button" variant="outline" onClick={handleDeleteCookies}>
              Delete optional cookies
            </Button>
          </Group>
          {cookieStatus && (
            <Alert color="green" role="status">
              {cookieStatus}
            </Alert>
          )}
        </Stack>
      </Box>

      <Box component="section" id="account-deletion" className={styles.actionCard}>
        <Stack gap="sm">
          <Title order={2}>Request account deletion</Title>
          <Text>
            Send us the email address used for your KURB account. We will contact you to verify
            ownership before deleting eligible account data. Records we must retain by law may be
            kept for the required period.
          </Text>
          <Text>
            Account deletion does not itself cancel an active paid subscription. Cancel your plan
            first using the subscription controls described in our terms.
          </Text>

          <form
            name={FORM_NAME}
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleDeletionRequest}
          >
            <Stack gap="md">
              <Input type="hidden" name="form-name" value={FORM_NAME} />
              <Input type="hidden" name="submitted-at" value="" />
              <Box className={styles.honeypot} aria-hidden="true">
                <TextInput
                  name="bot-field"
                  label="Do not fill this in"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </Box>
              <TextInput
                name="email"
                type="email"
                label="KURB account email"
                autoComplete="email"
                required
              />
              <Textarea
                name="reason"
                label="Anything else we should know?"
                description="Optional"
                rows={3}
              />
              <Checkbox
                name="confirmation"
                value="confirmed"
                required
                label="I understand that verified account deletion is permanent."
              />
              <Group>
                <Button type="submit" loading={formStatus === "submitting"}>
                  Send deletion request
                </Button>
              </Group>
            </Stack>
          </form>

          {formStatus === "success" && (
            <Alert color="green" role="status" title="Request received">
              We will contact the account email to verify ownership before deleting data.
            </Alert>
          )}
          {formStatus === "error" && (
            <Alert color="red" role="alert" title="We could not send the request">
              Please try again or email{" "}
              <Anchor href="mailto:contact@kurb.online?subject=Account%20deletion%20request">
                contact@kurb.online
              </Anchor>
              .
            </Alert>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default PrivacyActions;
